// src/components/Dashboard.jsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardData } from "../../components/dashboardApi";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardData()
      .then((data) => {
        setStats(data);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Помилка завантаження");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Завантаження...</p>;
  if (error) return <p>Помилка: {error}</p>;

  const { overview, charts, latest, supportLetters = {} } = stats;

  return (
    <div style={{ padding: "20px" }}>
      <h1>Дашборд електрокабінету</h1>

      {/* Верхні карточки */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
        <Card title="Користувачі" value={overview.totalUsers} />
        <Card title="Особові рахунки" value={overview.totalPersonalAccounts} />
        <Card
          title="Без світла"
          value={`${overview.accountsWithoutPower} (${overview.powerOutagePercent}%)`}
        />
        <Card title="Відкриті заявки" value={overview.openServiceOrders} />
        <Card title="Email не верифіковано" value={overview.usersUnverifiedEmail} />
        <Card title="Без особового рахунку" value={overview.usersWithoutAccount} />
      </div>

      {/* Листи підтримки */}
      <h2 style={{ marginTop: "24px", marginBottom: "8px" }}>Листи підтримки</h2>
      <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
        <LinkCard
          title="Не пререглянуто"
          value={supportLetters.new ?? 0}
          href="/support-letters"
          borderColor="#321fdb"
        />
        <LinkCard
          title="Переглянуто"
          value={supportLetters.viewed ?? 0}
          href="/support-letters"
          borderColor="#321fdb"
        />
        <LinkCard
          title="В роботі"
          value={supportLetters.in_progress ?? 0}
          href="/support-letters"
          borderColor="#f9b115"
        />
      </div>

      {/* Графіки як таблиці */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
        <Block title="Покази лічильників по днях">
          <Table
            columns={["Дата", "К-сть"]}
            rows={charts.readingsByDay.map((i) => [i.date, i.count])}
          />
        </Block>

        <Block title="Нові заявки по днях">
          <Table
            columns={["Дата", "К-сть"]}
            rows={charts.ordersByDay.map((i) => [i.date, i.count])}
          />
        </Block>
      </div>

      {/* Останні */}
      <div style={{ display: "flex", gap: "20px" }}>
        <Block title="Останні сповіщення">
          <Table
            columns={["Дата", "Тип", "Текст"]}
            rows={latest.notifications.map((n) => [
              n.createdAt,
              n.type,
              n.title,
            ])}
          />
        </Block>

        <Block title="Останні заявки">
          <Table
            columns={["Дата", "Тип", "Статус"]}
            rows={latest.serviceOrders.map((o) => [
              o.createdAt,
              o.type,
              o.status,
            ])}
          />
        </Block>
      </div>
    </div>
  );
}

// -------------------------------------------------
// UI HELPERS
// -------------------------------------------------

function Card({ title, value }) {
  return (
    <div
      style={{
        border: "1px solid #ddd",
        padding: "12px",
        borderRadius: "8px",
        width: "200px",
      }}
    >
      <div style={{ fontSize: "12px", opacity: 0.6 }}>{title}</div>
      <div style={{ fontSize: "24px", fontWeight: "bold" }}>{value}</div>
    </div>
  );
}

function LinkCard({ title, value, href, borderColor }) {
  return (
    <Link to={href} style={{ textDecoration: "none", color: "inherit" }}>
      <div
        style={{
          border: "1px solid #ddd",
          borderLeft: `4px solid ${borderColor || "#ddd"}`,
          padding: "12px",
          borderRadius: "8px",
          width: "200px",
          cursor: "pointer",
        }}
      >
        <div style={{ fontSize: "12px", opacity: 0.6 }}>{title}</div>
        <div style={{ fontSize: "24px", fontWeight: "bold" }}>{value}</div>
      </div>
    </Link>
  );
}

function Block({ title, children }) {
  return (
    <div style={{ flex: 1 }}>
      <h2 style={{ marginBottom: "8px" }}>{title}</h2>
      <div
        style={{
          border: "1px solid #ddd",
          borderRadius: "8px",
          padding: "10px",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Table({ columns, rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c}
              style={{
                borderBottom: "1px solid #ccc",
                textAlign: "left",
                paddingBottom: "4px",
              }}
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => (
          <tr key={idx}>
            {r.map((cell, i) => (
              <td key={i} style={{ padding: "4px 0" }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
